import { and, eq, gte, inArray, lte } from "drizzle-orm";
import {
  aisle,
  meal_plan_entry,
  pantry_item,
  type PowerSyncDrizzleDB,
  recipe,
  recipe_ingredient,
  recipe_version,
  shopping_item,
  shopping_list,
} from "@mealplan/powersync";
import { liveRecipeFilters } from "../live/filters";
import { readMatchingRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { parseVersionIngredients } from "../recipes/mutations";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { aggregateIngredients, aggregationKey, type PlannedIngredient } from "./aggregation";

/** Local today's `YYYY-MM-DD` (to decide last week vs current/future). */
function todayDateKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Checked state of the auto-generated items of a list that already exists,
 * keyed by `aggregationKey(name, unit)`, so that regenerating the week keeps
 * what the user already bought. Manual items are skipped: they are kept as-is
 * and never feed the carry-over.
 */
export function carryCheckedState(
  previousItems: readonly Pick<
    typeof shopping_item.$inferSelect,
    "name" | "unit" | "checked" | "is_manual"
  >[],
): Map<string, number> {
  const checkedByKey = new Map<string, number>();
  for (const item of previousItems) {
    if (item.is_manual === 1) {
      continue; // manual ones are kept as-is
    }
    if (item.name) {
      checkedByKey.set(aggregationKey(item.name, item.unit), item.checked ?? 0);
    }
  }
  return checkedByKey;
}

export interface GenerateShoppingListOptions {
  /** Monday of the week (YYYY-MM-DD). */
  weekStart: string;
  /** Sunday of the week (YYYY-MM-DD). */
  weekEnd: string;
  /** Name of the list if it needs to be created. */
  listName: string;
  /** Subtract user's pantry stock. */
  usePantry?: boolean;
}

export interface GenerateShoppingListResult {
  listId: string;
  /** Inserted auto-generated items. */
  generatedCount: number;
  /** Considered plan entries. */
  entryCount: number;
}

/**
 * Generates (or REGENERATES) the shopping list for a week from the meal plan.
 *
 * - Sums quantities of identical ingredients (see aggregation.ts) scaled by
 *   planned servings.
 * - Denormalizes the ingredient aisle name into `category`.
 * - If the week's list already exists: preserves manual items and
 *   the `checked` state of equivalent items (same name+unit key);
 *   previous auto-generated items are replaced.
 */
export async function generateShoppingListFromPlan(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: GenerateShoppingListOptions,
  verifiedWriteOptions: VerifiedWriteOptions = {},
): Promise<GenerateShoppingListResult> {
  // 1. Plan entries for the week (with the pointer/seal per dish).
  //    `recipe_id` is taken from the RECIPE (joined side), filtered by the live layer:
  //    an entry pointing to a trashed recipe gets recipe_id =
  //    null → does NOT feed the shopping list (neither its name nor its ingredients).
  //    Previously the join was raw (`eq(...)` without filter) and `recipe_id` came from
  //    meal_plan_entry, so a deleted recipe kept leaking its
  //    ingredients into the generated list (cohort bug of the scattered invariant).
  const entries = await db
    .select({
      recipe_id: recipe.id,
      servings: meal_plan_entry.servings,
      recipe_servings: recipe.servings,
      recipe_version_id: meal_plan_entry.recipe_version_id,
    })
    .from(meal_plan_entry)
    .leftJoin(recipe, and(eq(meal_plan_entry.recipe_id, recipe.id), ...liveRecipeFilters()))
    .where(
      and(
        eq(meal_plan_entry.user_id, userId),
        gte(meal_plan_entry.plan_date, options.weekStart),
        lte(meal_plan_entry.plan_date, options.weekEnd),
      ),
    );

  const recipeIds = [
    ...new Set(entries.map((e) => e.recipe_id).filter((r): r is string => Boolean(r))),
  ];

  // 2. LIVE ingredients from those recipes + user aisles.
  const ingredients =
    recipeIds.length > 0
      ? await db
          .select()
          .from(recipe_ingredient)
          .where(inArray(recipe_ingredient.recipe_id, recipeIds))
      : [];

  const aisles = await db.select().from(aisle).where(eq(aisle.user_id, userId));
  const aisleNameById = new Map(aisles.map((a) => [a.id, a.name]));

  const ingredientsByRecipe = new Map<string, typeof ingredients>();
  for (const ingredient of ingredients) {
    if (!ingredient.recipe_id) {
      continue;
    }
    const bucket = ingredientsByRecipe.get(ingredient.recipe_id) ?? [];
    bucket.push(ingredient);
    ingredientsByRecipe.set(ingredient.recipe_id, bucket);
  }

  // 2b. PRODUCT DECISION (pointer/seal): for PAST WEEKS
  //     (weekEnd < today) the list reflects what was ACTUALLY planned → the
  //     SEALED version of each dish is preferred (`recipe_version.ingredients`
  //     + its `servings` as recipeServings) when that row is available
  //     locally (within the sync window). If the seal fell outside the
  //     window (or the dish is legacy without a seal), it falls back to the
  //     LIVE ingredient. For the CURRENT/FUTURE week, the live recipe is ALWAYS used (the
  //     regeneration reflects today's recipes). Offline-safe: without the sealed
  //     row, falls back to live without failing.
  const isPastWeek = options.weekEnd < todayDateKey();
  const sealedIds = isPastWeek
    ? [...new Set(entries.map((e) => e.recipe_version_id).filter((v): v is string => Boolean(v)))]
    : [];
  const sealedVersions =
    sealedIds.length > 0
      ? await db.select().from(recipe_version).where(inArray(recipe_version.id, sealedIds))
      : [];
  const sealedById = new Map(sealedVersions.map((v) => [v.id, v]));

  // 3. Expand: each plan entry contributes its recipe's ingredients
  //    (sealed for past weeks, live otherwise) scaled by its
  //    servings.
  const planned: PlannedIngredient[] = [];
  for (const entry of entries) {
    if (!entry.recipe_id) {
      continue;
    }
    const sealed =
      isPastWeek && entry.recipe_version_id ? sealedById.get(entry.recipe_version_id) : undefined;
    if (sealed) {
      for (const ing of parseVersionIngredients(sealed.ingredients ?? null)) {
        planned.push({
          name: ing.name,
          quantity: ing.quantity,
          unit: ing.unit,
          category: ing.aisle_id ? (aisleNameById.get(ing.aisle_id) ?? null) : null,
          recipeServings: sealed.servings ?? 1,
          plannedServings: entry.servings ?? null,
        });
      }
      continue;
    }
    const recipeIngredients = ingredientsByRecipe.get(entry.recipe_id) ?? [];
    for (const ingredient of recipeIngredients) {
      planned.push({
        name: ingredient.name ?? "",
        quantity: ingredient.quantity ?? null,
        unit: ingredient.unit ?? null,
        category: ingredient.aisle_id ? (aisleNameById.get(ingredient.aisle_id) ?? null) : null,
        recipeServings: entry.recipe_servings ?? 1,
        plannedServings: entry.servings ?? null,
      });
    }
  }

  // 4. Despensa opcional.
  const pantry = options.usePantry
    ? (await db.select().from(pantry_item).where(eq(pantry_item.user_id, userId))).map((p) => ({
        name: p.name ?? "",
        quantity: p.quantity ?? null,
        unit: p.unit ?? null,
      }))
    : [];

  const aggregated = aggregateIngredients(planned, pantry);

  // 5. Upsert the week's list + replace auto-generated items.
  //
  // Decides INSERT-vs-UPDATE by CONTENT (week), not by known `id` — the
  // same Class 2 as `ensureAislesByNameMutation` (see `../shopping/mutations.ts`)
  // and the same window as a phantom UPDATE (`../powersync/verified-write.ts`).
  // The caller (`handleGenerateList` in the UI) triggers "Generate list" for the
  // week without knowing beforehand whether one already exists — so there is no `isNew`
  // to pass, and the decisive `SELECT` is reread with `readMatchingRowVerified`
  // (retry with backoff) BEFORE concluding that a new list needs to be created.
  // If the backoff is exhausted, it is the first time a list is generated for
  // that week: creating is correct.
  const now = nowISO();
  const existingList = await readMatchingRowVerified(
    () =>
      db
        .select()
        .from(shopping_list)
        .where(
          and(eq(shopping_list.user_id, userId), eq(shopping_list.week_start, options.weekStart)),
        )
        .limit(1),
    verifiedWriteOptions,
  );

  let listId: string;

  let checkedByKey = new Map<string, number>();
  if (existingList) {
    listId = existingList.id;
    const previousItems = await db
      .select()
      .from(shopping_item)
      .where(eq(shopping_item.shopping_list_id, listId));
    checkedByKey = carryCheckedState(previousItems);
  } else {
    listId = generateId();
  }

  await db.transaction(async (trx) => {
    if (existingList) {
      await trx
        .update(shopping_list)
        .set({ name: options.listName.trim(), updated_at: now })
        .where(eq(shopping_list.id, listId));
      await trx
        .delete(shopping_item)
        .where(and(eq(shopping_item.shopping_list_id, listId), eq(shopping_item.is_manual, 0)));
    } else {
      await trx.insert(shopping_list).values({
        id: listId,
        user_id: userId,
        name: options.listName.trim(),
        week_start: options.weekStart,
        created_at: now,
        updated_at: now,
      });
    }

    let order = 0;
    for (const item of aggregated) {
      await trx.insert(shopping_item).values({
        id: generateId(),
        shopping_list_id: listId,
        user_id: userId,
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        category: item.category,
        checked: checkedByKey.get(item.key) ?? 0,
        is_manual: 0,
        sort_order: order,
        created_at: now,
        updated_at: now,
      });
      order += 1;
    }
  });

  return { listId, generatedCount: aggregated.length, entryCount: entries.length };
}
